import { replacePathParams } from '../http/replacePathParams';

import {
  type GetShopProductsByStatusQueryParams,
  type GetShopProductsByStatusResponse,
  type ShopProductStatus,
} from './aliases';

import {
  SHOP_BY_USERNAME_ENDPOINT,
  SHOP_REARRANGE_BY_ID,
  SHOP_FILTER_AGGREGATES,
  SEARCH_SHOP_PRODUCTS_BY_USER_ID_ENDPOINT,
  SHOP_BY_ID_ENDPOINT,
  BATCH_SHOP_BY_USERNAME_ENDPOINT,
  SHOP_PRODUCTS_BY_SELLER_ID_ENDPOINT,
  PRODUCT_ACTIONS_ENDPOINT,
  SHOP_PRODUCTS_BY_STATUS_ENDPOINT,
} from '@/constants/endpoints';
import http, { typedHttp } from '@/modules/http';
import { type RequestBody } from '@/modules/http/type-helpers';
import { type AxiosCompatibleResponse } from '@/modules/http/types';
import {
  type GetShopFilterAggregatesQueryParams,
  type GetShopProductsQueryParams,
  type GetShopProductsResponse,
} from '@/modules/shop/aliases';
import {
  type ShopByUsernameResponse,
  type FetchFilterAggregatesResponse,
  type ShopProductsSearchRequest,
  type ShopProductsSearchResponse,
  type FetchBatchShopByUsernameResponse,
} from '@/modules/shop/types';

export function fetchShopByUsername(
  username: string
): Promise<AxiosCompatibleResponse<ShopByUsernameResponse>> {
  return http.get(SHOP_BY_USERNAME_ENDPOINT.replace(':username', username));
}

export function fetchShop(userId: string) {
  return typedHttp.get(
    replacePathParams(SHOP_BY_ID_ENDPOINT, { sellerId: userId })
  );
}

export function fetchBatchShopByUsername(
  usernames: string[],
  includeProducts = false
): Promise<AxiosCompatibleResponse<FetchBatchShopByUsernameResponse>> {
  const usernameList = usernames.map((username) => username.trim()).join(',');

  return http.get(
    BATCH_SHOP_BY_USERNAME_ENDPOINT.replace(':usernames', usernameList),
    {
      params: {
        includeProducts: includeProducts ? 'true' : 'false',
      },
    }
  );
}

export function fetchShopProducts(
  sellerId: string | number,
  params?: GetShopProductsQueryParams
): Promise<AxiosCompatibleResponse<GetShopProductsResponse>> {
  return typedHttp.get(
    replacePathParams(SHOP_PRODUCTS_BY_SELLER_ID_ENDPOINT, {
      sellerId: String(sellerId),
    }),
    {
      params,
      withAuth: true,
      withDeviceId: true,
    }
  );
}

export function fetchSearchProducts(
  userId: string | number,
  req?: ShopProductsSearchRequest
): Promise<AxiosCompatibleResponse<ShopProductsSearchResponse>> {
  return http.get(
    SEARCH_SHOP_PRODUCTS_BY_USER_ID_ENDPOINT.replace(':userId', String(userId)),
    {
      params: req?.params,
      withAuth: true,
    }
  );
}

export function fetchFilterAggregates({
  sellerId,
  params,
}: {
  sellerId: string | number;
  params: GetShopFilterAggregatesQueryParams;
}): Promise<AxiosCompatibleResponse<FetchFilterAggregatesResponse>> {
  // TODO(typedhttp): OpenAPI spec is missing `groups` field on response spec,
  // update to align and use typedHttp here.
  // eslint-disable-next-line local-rules/use-typed-http
  return http.get(
    replacePathParams(SHOP_FILTER_AGGREGATES, {
      sellerId: sellerId.toString(),
    }),
    {
      params,
      withAuth: true,
    }
  );
}

export function fetchShopProductsByStatus(
  sellerId: number | string,
  params: GetShopProductsByStatusQueryParams,
  status: ShopProductStatus
): Promise<AxiosCompatibleResponse<GetShopProductsByStatusResponse>> {
  return typedHttp.get(
    replacePathParams(SHOP_PRODUCTS_BY_STATUS_ENDPOINT, {
      sellerId: String(sellerId),
      status,
    }),
    {
      withAuth: true,
      withDeviceId: true,
      params,
    }
  );
}

export function rearrangeProducts(
  data: RequestBody<typeof SHOP_REARRANGE_BY_ID, 'patch'>
) {
  return typedHttp.patch(SHOP_REARRANGE_BY_ID, data, {
    withAuth: true,
  });
}

export function getProductActions() {
  return typedHttp.get(PRODUCT_ACTIONS_ENDPOINT, {
    withAuth: true,
  });
}
